import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MENU_STRUCTURE, CONTACT_INFO } from '../data';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeMobileSub, setActiveMobileSub] = useState(null);
  const location = useLocation();
  const timeoutRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setActiveMobileSub(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [location]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (label) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  // Helper to check if a menu item (or any of its nested children) is active
  const isItemActive = (item) => {
    if (location.pathname === item.href) return true;
    if (item.children) {
      return item.children.some(child => {
        if (location.pathname === child.href) return true;
        if (child.children) {
          return child.children.some(grandchild => location.pathname === grandchild.href);
        }
        return false;
      });
    }
    return false;
  };

  return (
    <header className="relative z-50 w-full bg-white shadow-sm">
      {/* Top Bar */}
      <div className="bg-secondary text-white text-xs py-2.5 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-1">
            <span className="flex items-center gap-2">
              <i className="fa-light fa-phone text-primary" />
              <span>Marianne: <a href={`tel:${CONTACT_INFO.mariannePhone}`} className="hover:text-primary transition-colors font-medium">{CONTACT_INFO.mariannePhone}</a></span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 hover:text-primary transition-colors">
              <i className="fa-light fa-envelope text-primary" />
              <span>{CONTACT_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between h-24">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="https://assets.auxodev.dk/assets/c39928d5-b2a6-4904-83a9-11ba7b5b852e/untitled-1-03-0.jpg"
                alt="Vester-Bo Logo"
                className="h-16 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <nav className="hidden lg:flex space-x-1 items-center">
            {MENU_STRUCTURE.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isActive = isItemActive(item);

              if (item.isMega) {
                return (
                  <div
                    key={item.label}
                    className="static"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors outline-none ${
                        isActive ? 'text-primary bg-teal-50/50' : 'text-slate-700 hover:text-primary hover:bg-slate-50'
                      }`}
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Mega Dropdown Menu */}
                    {activeDropdown === item.label && (
                      <div className="absolute left-4 right-4 lg:left-8 lg:right-8 top-full mt-1 bg-white rounded-3xl shadow-2xl border border-slate-100/80 p-8 z-50 grid grid-cols-12 gap-8 animate-in fade-in slide-in-from-top-2 duration-200 before:absolute before:-top-12 before:left-0 before:right-0 before:h-12 before:content-['']">
                        {/* Column 1: Om Vester-Bo (col-span-4) */}
                        <div className="col-span-4 border-r border-slate-100 pr-6">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center text-primary">
                              <i className="fa-solid fa-circle-info text-lg" />
                            </div>
                            <div>
                              <h4 className="font-serif text-base font-bold text-secondary">
                                Om Vester-Bo
                              </h4>
                              <p className="text-xs text-slate-400">Hvem er vi, og hvad står vi for?</p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                            Læs om vores målgruppe, formål, værdigrundlag og tilsynsrapporter. Vi skaber trygge rammer for udvikling.
                          </p>
                          <div className="space-y-1">
                            {item.children[0].children.map((subItem) => (
                              <Link
                                key={subItem.label}
                                to={subItem.href}
                                className={`group flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-all ${
                                  location.pathname === subItem.href
                                    ? 'text-primary bg-teal-50/50 font-semibold'
                                    : 'text-slate-600 hover:text-primary hover:bg-slate-50'
                                }`}
                              >
                                <span>{subItem.label}</span>
                                <i className="fa-solid fa-chevron-right text-[10px] opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                              </Link>
                            ))}
                          </div>
                        </div>

                        {/* Column 2: Vester-Bo tilbyder (col-span-5) */}
                        <div className="col-span-5 border-r border-slate-100 pr-6">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center text-primary">
                              <i className="fa-solid fa-hand-holding-heart text-lg" />
                            </div>
                            <div>
                              <h4 className="font-serif text-base font-bold text-secondary">
                                Vester-Bo tilbyder
                              </h4>
                              <p className="text-xs text-slate-400">Vores pædagogiske tilbud og rammer</p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                            Vi tilbyder en bred vifte af aktiviteter, herunder miljøterapi, samtaler, NADA, beskæftigelse og sund livsstil.
                          </p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {item.children[1].children.map((subItem) => (
                              <Link
                                key={subItem.label}
                                to={subItem.href}
                                className={`group flex items-center justify-between px-3 py-1.5 rounded-lg text-xs transition-all ${
                                  location.pathname === subItem.href
                                    ? 'text-primary bg-teal-50/50 font-semibold'
                                    : 'text-slate-600 hover:text-primary hover:bg-slate-50'
                                }`}
                              >
                                <span className="truncate">{subItem.label}</span>
                                <i className="fa-solid fa-chevron-right text-[8px] opacity-0 group-hover:opacity-100 transition-opacity text-primary flex-shrink-0 ml-1" />
                              </Link>
                            ))}
                          </div>
                        </div>

                        {/* Column 3: Featured Card / Quick Contact (col-span-3) */}
                        <div className="col-span-3 flex flex-col justify-between bg-slate-50/50 rounded-2xl p-5 border border-slate-100">
                          <div>
                            <span className="inline-block px-2.5 py-1 rounded-full bg-teal-50 text-primary text-[10px] font-bold tracking-wider uppercase mb-3">
                              Kontakt os
                            </span>
                            <h4 className="font-serif text-base font-bold text-secondary mb-2">
                              Har du spørgsmål?
                            </h4>
                            <p className="text-xs text-slate-500 leading-relaxed mb-4">
                              Vi sidder klar til at besvare dine spørgsmål om pladser, visitation og vores tilbud.
                            </p>
                            
                            <div className="space-y-2.5 mb-4">
                              <div className="flex items-center gap-2.5 text-xs text-slate-600">
                                <i className="fa-solid fa-phone text-primary w-4 text-center" />
                                <span>Marianne: <a href={`tel:${CONTACT_INFO.mariannePhone}`} className="hover:text-primary font-medium">{CONTACT_INFO.mariannePhone}</a></span>
                              </div>
                            </div>
                          </div>

                          <Link
                            to="/contact"
                            className="w-full text-center py-2.5 rounded-xl text-xs font-semibold text-white bg-primary hover:bg-primary-dark shadow-sm hover:shadow transition-all"
                          >
                            Kontakt os her
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              if (hasChildren) {
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors outline-none ${
                        isActive ? 'text-primary bg-teal-50/50' : 'text-slate-700 hover:text-primary hover:bg-slate-50'
                      }`}
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {activeDropdown === item.label && (
                      <div className="absolute left-0 mt-0 w-64 rounded-xl bg-white shadow-xl border border-slate-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200 before:absolute before:-top-4 before:left-0 before:right-0 before:h-4 before:content-['']">
                        <Link
                          to={item.href}
                          className="block px-4 py-2.5 text-sm font-semibold text-secondary hover:bg-teal-50/50 hover:text-primary border-b border-slate-50"
                        >
                          Oversigt: {item.label}
                        </Link>
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className={`block px-4 py-2 text-sm transition-colors ${
                              location.pathname === child.href ? 'text-primary bg-teal-50/30 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-primary'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === item.href ? 'text-primary bg-teal-50/50' : 'text-slate-700 hover:text-primary hover:bg-slate-50'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            <Link
              to="/contact"
              className={`ml-4 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-primary hover:bg-primary-dark shadow-md hover:shadow-lg transition-all duration-200`}
            >
              Kontakt os
            </Link>
          </nav>

          {/* Mobile menu button */}
          <div className="flex items-center lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2.5 rounded-xl text-slate-700 hover:text-primary hover:bg-slate-50 focus:outline-none transition-colors border border-slate-100"
              aria-expanded="false"
            >
              <span className="sr-only">Åbn hovedmenu</span>
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-inner max-h-[calc(100vh-96px)] overflow-y-auto">
          <div className="px-4 pt-3 pb-6 space-y-1">
            {MENU_STRUCTURE.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isDropdownActive = activeDropdown === item.label;

              if (item.isMega) {
                return (
                  <div key={item.label} className="space-y-1">
                    <button
                      onClick={() => setActiveDropdown(isDropdownActive ? null : item.label)}
                      className="w-full flex justify-between items-center px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownActive ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownActive && (
                      <div className="pl-4 pr-2 py-1.5 space-y-2 bg-slate-50/50 rounded-2xl">
                        {item.children.map((subCategory) => {
                          const isSubActive = activeMobileSub === subCategory.label;
                          return (
                            <div key={subCategory.label} className="space-y-1">
                              <button
                                onClick={() => setActiveMobileSub(isSubActive ? null : subCategory.label)}
                                className="w-full flex justify-between items-center px-4 py-2.5 rounded-xl text-sm font-semibold text-secondary hover:bg-slate-100/50 transition-colors"
                              >
                                <span>{subCategory.label}</span>
                                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${isSubActive ? 'rotate-180' : ''}`} />
                              </button>

                              {isSubActive && (
                                <div className="pl-4 pr-2 py-1 space-y-1 bg-white rounded-xl border border-slate-100/60 mt-1">
                                  <Link
                                    to={subCategory.href}
                                    className="block px-4 py-2 text-xs font-bold text-secondary hover:text-primary border-b border-slate-50"
                                  >
                                    Oversigt: {subCategory.label}
                                  </Link>
                                  {subCategory.children.map((child) => (
                                    <Link
                                      key={child.label}
                                      to={child.href}
                                      className={`block px-4 py-2 rounded-lg text-xs transition-colors ${
                                        location.pathname === child.href ? 'text-primary bg-teal-50/50 font-semibold' : 'text-slate-600 hover:text-primary'
                                      }`}
                                    >
                                      {child.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              if (hasChildren) {
                return (
                  <div key={item.label} className="space-y-1">
                    <button
                      onClick={() => setActiveDropdown(isDropdownActive ? null : item.label)}
                      className="w-full flex justify-between items-center px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownActive ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownActive && (
                      <div className="pl-4 pr-2 py-1 space-y-1 bg-slate-50/50 rounded-2xl">
                        <Link
                          to={item.href}
                          className="block px-4 py-2.5 text-sm font-semibold text-secondary hover:text-primary"
                        >
                          Oversigt: {item.label}
                        </Link>
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${
                              location.pathname === child.href ? 'text-primary bg-teal-50/50 font-semibold' : 'text-slate-600 hover:text-primary'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === item.href ? 'text-primary bg-teal-50/50 font-semibold' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            <div className="pt-4 px-4">
              <Link
                to="/contact"
                className="block w-full text-center px-5 py-3 rounded-full text-base font-semibold text-white bg-primary hover:bg-primary-dark shadow-md transition-colors"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
